import { ChangeDetectionStrategy, Component, Input, OnChanges, ViewChild } from '@angular/core';
import { CommissionEstimateTableColumns } from '@app/shared/constants';
import { FilterType, LocationStatusEnum } from '@app/shared/enums/common.enums';
import { Filter, SortState, TableColumn } from '@app/shared/models/common.model';
import { CommissionEstimateLocation } from '@app/shared/models/commission-estimate-report.model';
import { exportToExcel } from '@app/shared/utilities/excel-export';
import { LOCATION_STATUS_META, statusOptions } from '@app/shared/utilities/status.meta';
import { FilterDrawerComponent } from '../filter-drawer/filter-drawer.component';

@Component({
  selector: 'app-commission-estimate-table',
  templateUrl: './commission-estimate-table.component.html',
  styleUrl: '../volume-table/volume-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommissionEstimateTableComponent implements OnChanges {
  @Input() locations: CommissionEstimateLocation[] = [];
  @Input() usePayoutTerminology = false;
  @ViewChild(FilterDrawerComponent) private drawer?: FilterDrawerComponent;

  readonly initialSort: SortState = { key: 'postTaxAmount', dir: 'desc' };
  readonly filters: Map<string, Filter> = new Map([
    ['provinceId', { type: FilterType.MultiSelect, options: [] }],
    ['statusId', { type: FilterType.MultiSelect, options: [] }],
  ]);
  columns: Map<string, TableColumn> = new Map(CommissionEstimateTableColumns);
  rows: CommissionEstimateLocation[] = [];
  footerRow: Record<string, any> | null = null;
  searchText = '';
  private selectedProvinces: string[] = [];
  private selectedStatuses: number[] = [];

  get activeFilterCount(): number {
    return this.selectedProvinces.length + this.selectedStatuses.length;
  }

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  ngOnChanges(): void {
    this.columns = new Map(CommissionEstimateTableColumns);
    if (this.usePayoutTerminology) {
      this.columns.set('postTaxAmount', {
        ...this.columns.get('postTaxAmount')!,
        label: 'App.Admin.Dashboard.Commission.EstimatedPayout',
      });
    }
    this.updateFilterOptions();
    this.recompute();
  }

  onSearch(value: string): void {
    this.searchText = value ?? '';
    this.recompute();
  }

  clearSearch(): void {
    this.onSearch('');
  }

  toggleFilters(): void {
    this.drawer?.toggleFilters();
  }

  applyFilters(value: any): void {
    this.selectedProvinces = (value?.provinceId ?? []).map((option: any) => option.id);
    this.selectedStatuses = (value?.statusId ?? []).map((option: any) => option.id);
    this.drawer?.toggleFilters();
    this.recompute();
  }

  clearFilters(isOpen: boolean = true): void {
    this.selectedProvinces = [];
    this.selectedStatuses = [];
    if (!isOpen) this.drawer?.filtersForm?.reset();
    this.recompute();
  }

  statusClass(statusId: number): string {
    return LOCATION_STATUS_META[statusId].css;
  }

  statusLabel(statusId: number): string {
    return LOCATION_STATUS_META[statusId].key;
  }

  isActive(statusId: number): boolean {
    return statusId === LocationStatusEnum.Active;
  }

  export(): void {
    const amountHeading = this.usePayoutTerminology ? 'Estimated payout' : 'Estimated commission';
    const data = this.rows.map((row) => ({
      'Partner shop': row.storeName,
      City: row.city,
      Province: row.provinceCode,
      'Pre-tax': row.preTaxAmount,
      Tax: row.taxAmount,
      [amountHeading]: row.postTaxAmount,
    }));
    exportToExcel(
      data,
      this.usePayoutTerminology ? 'partner-payouts-by-location.xlsx' : 'commission-estimates-by-location.xlsx',
      this.usePayoutTerminology ? 'Partner payouts' : 'Commission estimates'
    );
  }

  private updateFilterOptions(): void {
    const provinces = Array.from(new Set(this.locations.map((location) => location.provinceCode).filter(Boolean))).sort();
    const statusIds = Array.from(new Set(this.locations.map((location) => location.statusId))).sort((a, b) => a - b);
    this.filters.get('provinceId')!.options = provinces.map((province) => ({ id: province, value: province }));
    this.filters.get('statusId')!.options = statusOptions(LOCATION_STATUS_META).filter((option) => statusIds.includes(option.id));
  }

  private recompute(): void {
    let rows = this.locations;
    if (this.selectedProvinces.length) rows = rows.filter((row) => this.selectedProvinces.includes(row.provinceCode));
    if (this.selectedStatuses.length) rows = rows.filter((row) => this.selectedStatuses.includes(row.statusId));
    const search = this.searchText.trim().toLowerCase();
    if (search) {
      rows = rows.filter((row) => `${row.storeName} ${row.city} ${row.provinceCode}`.toLowerCase().includes(search));
    }
    this.rows = rows;
    this.footerRow = this.buildFooter();
  }

  private buildFooter(): Record<string, any> | null {
    if (!this.rows.length) return null;
    return {
      count: this.rows.length,
      preTaxAmount: this.columnTotal('preTaxAmount'),
      taxAmount: this.columnTotal('taxAmount'),
      postTaxAmount: this.columnTotal('postTaxAmount'),
    };
  }

  private columnTotal(key: 'preTaxAmount' | 'taxAmount' | 'postTaxAmount'): number {
    return this.rows.reduce((sum, row) => sum + row[key], 0);
  }
}
